import React, { useCallback, useEffect, useMemo, useState } from "react"
import StatusPill from "../shared/components/StatusPill"
import SectionCard from "../shared/components/SectionCard"
import { ui } from "../shared/styles/uiSystem"
import { apiFetch } from "../utils/apiFetch"
import {
  buttonStyle,
  primaryButtonStyle,
  inputStyle,
  textareaStyle,
} from "../features/clinical-history/components/ClinicalHistoryStyles"
import {
  SummaryTile,
  EmptyStateBlock,
} from "../features/clinical-history/components/ClinicalHistoryUiBlocks"
import {
  getCaseStatusLabel,
  getCaseStatusTone,
} from "../features/clinical-history/components/ClinicalHistoryStatusHelpers"

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

const SPECIALTY_META = {
  ENDO: {
    title: "Endodoncia",
    description: "Casos clÃ­nicos relacionados con tratamiento pulpar y periapical.",
    tone: "warning",
  },
  PERIO: {
    title: "Periodoncia",
    description: "Casos clÃ­nicos periodontales activos y cerrados.",
    tone: "success",
  },
  SURGERY: {
    title: "CirugÃ­a",
    description: "Casos clÃ­nicos quirÃºrgicos asociados al paciente.",
    tone: "danger",
  },
  ORTHO: {
    title: "Ortodoncia",
    description: "Casos ortodÃ³nticos, seguimiento y planeaciÃ³n.",
    tone: "info",
  },
  PEDIATRIC: {
    title: "OdontopediatrÃ­a",
    description: "Casos clÃ­nicos pediÃ¡tricos y seguimiento.",
    tone: "success",
  },
}

function getInitialCaseFormBySpecialty(type) {
  switch (type) {
    case "ENDO":
      return {
        tooth: "",
        surface: "ALL",
        chiefReason: "",
        priority: "MEDIA",
      }

    case "SURGERY":
      return {
        tooth: "",
        surface: "ALL",
        chiefReason: "",
        procedureType: "",
        priority: "MEDIA",
      }

    case "ORTHO":
      return {
        chiefReason: "",
        treatmentObjectives: "",
        priority: "MEDIA",
      }

    case "PERIO":
      return {
        chiefReason: "",
        periodontalDiagnosis: "",
        riskLevel: "",
        priority: "MEDIA",
      }

    case "PEDIATRIC":
      return {
        chiefReason: "",
        treatmentPlan: "",
        cariesRisk: "",
        priority: "MEDIA",
      }

    default:
      return {
        tooth: "",
        surface: "ALL",
        chiefReason: "",
        priority: "MEDIA",
      }
  }
}

function validateCaseFormBySpecialty(type, form) {
  switch (type) {
    case "ENDO":
      if (!form.tooth?.trim()) return "Indica la pieza dental."
      if (!form.chiefReason?.trim()) return "Escribe el motivo clÃ­nico."
      return ""

    case "SURGERY":
      if (!form.tooth?.trim() && !form.procedureType?.trim()) {
        return "Indica una pieza o el procedimiento quirÃºrgico."
      }
      if (!form.chiefReason?.trim()) return "Escribe el motivo clÃ­nico."
      return ""

    case "ORTHO":
      return form.chiefReason?.trim() || form.treatmentObjectives?.trim()
        ? ""
        : "Escribe el motivo clÃ­nico o los objetivos del tratamiento."

    case "PERIO":
      return form.chiefReason?.trim() || form.periodontalDiagnosis?.trim()
        ? ""
        : "Escribe el motivo clÃ­nico o el diagnÃ³stico periodontal."

    case "PEDIATRIC":
      return form.chiefReason?.trim() || form.treatmentPlan?.trim()
        ? ""
        : "Escribe el motivo clÃ­nico o el plan de tratamiento."

    default:
      return ""
  }
}

function buildCasePayload({
  clinicId,
  patientId,
  specialtyType,
  createForm,
  metaTitle,
}) {
  const payload = {
    clinicId,
    patientId,
    specialtyType,
    chiefReason: createForm.chiefReason?.trim() || "",
    priority: createForm.priority || "MEDIA",
    origin: "MANUAL",
  }

  switch (specialtyType) {
    case "ENDO":
      payload.title = createForm.tooth?.trim()
        ? `Caso de ${metaTitle} Â· Pieza ${createForm.tooth.trim()}`
        : `Caso de ${metaTitle}`

      payload.teeth = [
        {
          tooth: createForm.tooth.trim(),
          surface: createForm.surface || "ALL",
        },
      ]
      break

    case "SURGERY":
      payload.title =
        createForm.procedureType?.trim() ||
        (createForm.tooth?.trim()
          ? `Caso de ${metaTitle} Â· Pieza ${createForm.tooth.trim()}`
          : `Caso de ${metaTitle}`)

      if (createForm.tooth?.trim()) {
        payload.teeth = [
          {
            tooth: createForm.tooth.trim(),
            surface: createForm.surface || "ALL",
          },
        ]
      }
      break

    case "ORTHO":
      payload.title =
        createForm.treatmentObjectives?.trim() || "Caso de Ortodoncia"
      break

    case "PERIO":
      payload.title =
        createForm.periodontalDiagnosis?.trim() || "Caso de Periodoncia"
      break

    case "PEDIATRIC":
      payload.title =
        createForm.treatmentPlan?.trim() || "Caso de OdontopediatrÃ­a"
      break

    default:
      payload.title = `Caso de ${metaTitle}`
      break
  }

  return payload
}

function CaseCard({ item, onOpenCase }) {
  const teethLabel =
    item?.teeth?.length > 0
      ? item.teeth
          .map((tooth) =>
            tooth.surface && tooth.surface !== "ALL"
              ? `${tooth.tooth}-${tooth.surface}`
              : tooth.tooth
          )
          .join(", ")
      : "Sin pieza definida"

  return (
    <div
      style={{
        ...ui.card,
        borderRadius: 18,
        padding: 16,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <StatusPill tone={getCaseStatusTone(item.status)}>
          {getCaseStatusLabel(item.status)}
        </StatusPill>

        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            fontWeight: 700,
          }}
        >
          {item.lastActivityAt
            ? `Ãšltima actividad: ${new Date(item.lastActivityAt).toLocaleDateString("es-MX")}`
            : "Sin actividad reciente"}
        </div>
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <div
          style={{
            fontSize: 17,
            fontWeight: 900,
            color: "#0f172a",
            lineHeight: 1.2,
          }}
        >
          {item.title}
        </div>

        <div
          style={{
            fontSize: 13.5,
            color: "#475569",
            lineHeight: 1.55,
          }}
        >
          {item.chiefReason || "Sin motivo clÃ­nico registrado todavÃ­a."}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0,1fr))",
          gap: 10,
          alignItems: "stretch",
        }}
      >
        <SummaryTile
          title="Piezas"
          value={teethLabel}
          subtitle="Pieza o referencia asociada"
          tone="neutral"
        />

        <SummaryTile
          title="Evoluciones"
          value={String(item?._count?.evolutions || 0)}
          subtitle="Registros dentro del caso"
          tone={item?._count?.evolutions > 0 ? "info" : "neutral"}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button onClick={() => onOpenCase(item)} style={buttonStyle}>
          Abrir caso
        </button>
      </div>
    </div>
  )
}

export default function ClinicalHistorySpecialtyCasesPage({
  clinicId,
  patientId,
  patientDisplayName,
  specialtyType,
  onBackToSummary,
  onOpenCase,
}) {
  const meta = SPECIALTY_META[specialtyType] || SPECIALTY_META.ENDO

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [casesData, setCasesData] = useState({ active: [], closed: [], total: 0 })

  const [showCreatePanel, setShowCreatePanel] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState(() =>
    getInitialCaseFormBySpecialty(specialtyType)
  )

  useEffect(() => {
    setCreateForm(getInitialCaseFormBySpecialty(specialtyType))
  }, [specialtyType])

  const loadCases = useCallback(async () => {
    try {
      setLoading(true)
      setError("")

      const res = await apiFetch(
        `${API_BASE}/clinical-specialty-cases/patient/${patientId}/${specialtyType}?clinicId=${clinicId}`
      )

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.message || "No se pudieron cargar los casos clÃ­nicos.")
      }

      setCasesData({
        active: Array.isArray(data?.active) ? data.active : [],
        closed: Array.isArray(data?.closed) ? data.closed : [],
        total: Number(data?.total || 0),
      })
    } catch (err) {
      setError(err?.message || "No se pudieron cargar los casos clÃ­nicos.")
    } finally {
      setLoading(false)
    }
  }, [clinicId, patientId, specialtyType])

  useEffect(() => {
    if (!clinicId || !patientId || !specialtyType) return
    loadCases()
  }, [clinicId, patientId, specialtyType, loadCases])

  const handleCreateCase = async () => {
    try {
      const validationError = validateCaseFormBySpecialty(specialtyType, createForm)

      if (validationError) {
        alert(validationError)
        return
      }

      setCreating(true)

      const payload = buildCasePayload({
        clinicId,
        patientId,
        specialtyType,
        createForm,
        metaTitle: meta.title,
      })

      const res = await apiFetch(`${API_BASE}/clinical-specialty-cases`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.message || "No se pudo crear el caso.")
      }

      setShowCreatePanel(false)
      setCreateForm(getInitialCaseFormBySpecialty(specialtyType))

      await loadCases()
    } catch (err) {
      alert(err?.message || "No se pudo crear el caso.")
    } finally {
      setCreating(false)
    }
  }

  const summaryText = useMemo(() => {
    return `${casesData.active.length} activos Â· ${casesData.closed.length} cerrados`
  }, [casesData])

  return (
    <div style={{ padding: 24, background: "#f8fafc", minHeight: "100vh" }}>
      <div
        style={{
          display: "grid",
          gap: 18,
          alignContent: "start",
        }}
      >
        <div
          style={{
            ...ui.card,
            borderRadius: 22,
            padding: 16,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 420px)",
              gap: 14,
              alignItems: "end",
            }}
          >
            <div
              style={{
                display: "grid",
                gap: 8,
                alignContent: "start",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#64748b",
                }}
              >
                Casos clÃ­nicos
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 900,
                    color: "#0f172a",
                    lineHeight: 1.02,
                    letterSpacing: "-0.03em",
                    wordBreak: "break-word",
                  }}
                >
                  {meta.title}
                </div>

                <StatusPill tone={meta.tone}>{summaryText}</StatusPill>
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: "#64748b",
                  lineHeight: 1.45,
                  maxWidth: 760,
                }}
              >
                {meta.description} Paciente actual:{" "}
                <strong>{patientDisplayName}</strong>.
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gap: 10,
                alignContent: "start",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                <SummaryTile
                  title="Activos"
                  value={String(casesData.active.length)}
                  subtitle="Casos abiertos o en seguimiento"
                  tone={casesData.active.length > 0 ? "info" : "neutral"}
                />

                <SummaryTile
                  title="Cerrados"
                  value={String(casesData.closed.length)}
                  subtitle="Casos completados o cancelados"
                  tone={casesData.closed.length > 0 ? "success" : "neutral"}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button onClick={onBackToSummary} style={buttonStyle}>
                  Volver al resumen
                </button>

                <button
                  onClick={() => setShowCreatePanel((prev) => !prev)}
                  style={primaryButtonStyle}
                >
                  {showCreatePanel ? "Cerrar panel" : "Nuevo caso"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {showCreatePanel ? (
          <SectionCard
            title="Nuevo caso"
            subtitle="Crea un caso clÃ­nico de esta especialidad para el paciente actual"
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              {(specialtyType === "ENDO" || specialtyType === "SURGERY") && (
                <>
                  <div>
                    <div style={ui.label}>Pieza</div>
                    <input
                      value={createForm.tooth || ""}
                      onChange={(e) =>
                        setCreateForm((prev) => ({ ...prev, tooth: e.target.value }))
                      }
                      placeholder="Ej. 16"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <div style={ui.label}>Superficie</div>
                    <select
                      value={createForm.surface || "ALL"}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          surface: e.target.value,
                        }))
                      }
                      style={inputStyle}
                    >
                      <option value="ALL">General</option>
                      <option value="O">Oclusal</option>
                      <option value="M">Mesial</option>
                      <option value="D">Distal</option>
                      <option value="V">Vestibular</option>
                      <option value="L">Lingual</option>
                      <option value="P">Palatina</option>
                    </select>
                  </div>
                </>
              )}

              {specialtyType === "SURGERY" ? (
                <div>
                  <div style={ui.label}>Procedimiento quirÃºrgico</div>
                  <input
                    value={createForm.procedureType || ""}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        procedureType: e.target.value,
                      }))
                    }
                    placeholder="Ej. Exodoncia de tercer molar"
                    style={inputStyle}
                  />
                </div>
              ) : null}

              {specialtyType === "ORTHO" ? (
                <div>
                  <div style={ui.label}>Objetivos del tratamiento</div>
                  <input
                    value={createForm.treatmentObjectives || ""}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        treatmentObjectives: e.target.value,
                      }))
                    }
                    placeholder="Ej. AlineaciÃ³n y correcciÃ³n de mordida"
                    style={inputStyle}
                  />
                </div>
              ) : null}

              {specialtyType === "PERIO" ? (
                <>
                  <div>
                    <div style={ui.label}>DiagnÃ³stico periodontal</div>
                    <input
                      value={createForm.periodontalDiagnosis || ""}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          periodontalDiagnosis: e.target.value,
                        }))
                      }
                      placeholder="Ej. Gingivitis inducida por placa"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <div style={ui.label}>Riesgo</div>
                    <input
                      value={createForm.riskLevel || ""}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          riskLevel: e.target.value,
                        }))
                      }
                      placeholder="Ej. Moderado"
                      style={inputStyle}
                    />
                  </div>
                </>
              ) : null}

              {specialtyType === "PEDIATRIC" ? (
                <>
                  <div>
                    <div style={ui.label}>Plan de tratamiento</div>
                    <input
                      value={createForm.treatmentPlan || ""}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          treatmentPlan: e.target.value,
                        }))
                      }
                      placeholder="Ej. Control preventivo y selladores"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <div style={ui.label}>Riesgo de caries</div>
                    <input
                      value={createForm.cariesRisk || ""}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          cariesRisk: e.target.value,
                        }))
                      }
                      placeholder="Ej. Alto"
                      style={inputStyle}
                    />
                  </div>
                </>
              ) : null}

              <div>
                <div style={ui.label}>Prioridad</div>
                <select
                  value={createForm.priority || "MEDIA"}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      priority: e.target.value,
                    }))
                  }
                  style={inputStyle}
                >
                  <option value="BAJA">Baja</option>
                  <option value="MEDIA">Media</option>
                  <option value="ALTA">Alta</option>
                </select>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <div style={ui.label}>Motivo clÃ­nico</div>
                <textarea
                  value={createForm.chiefReason || ""}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      chiefReason: e.target.value,
                    }))
                  }
                  placeholder="Escribe el motivo clÃ­nico del caso"
                  style={textareaStyle}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                marginTop: 14,
              }}
            >
              <button
                onClick={() => {
                  setShowCreatePanel(false)
                  setCreateForm(getInitialCaseFormBySpecialty(specialtyType))
                }}
                style={buttonStyle}
              >
                Cancelar
              </button>

              <button
                onClick={handleCreateCase}
                disabled={creating}
                style={{
                  ...primaryButtonStyle,
                  cursor: creating ? "not-allowed" : "pointer",
                  opacity: creating ? 0.75 : 1,
                }}
              >
                {creating ? "Creando..." : "Crear caso"}
              </button>
            </div>
          </SectionCard>
        ) : null}

        <SectionCard
          title="Casos activos"
          subtitle="Casos clÃ­nicos que siguen abiertos o en seguimiento"
        >
          {loading ? (
            <EmptyStateBlock text="Cargando casos activos..." />
          ) : error ? (
            <EmptyStateBlock text={error} />
          ) : casesData.active.length ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: 12,
              }}
            >
              {casesData.active.map((item) => (
                <CaseCard key={item.id} item={item} onOpenCase={onOpenCase} />
              ))}
            </div>
          ) : (
            <EmptyStateBlock text="No hay casos activos de esta especialidad para el paciente actual." />
          )}
        </SectionCard>

        <SectionCard
          title="Casos cerrados"
          subtitle="Casos completados o cancelados"
        >
          {loading ? (
            <EmptyStateBlock text="Cargando casos cerrados..." />
          ) : error ? (
            <EmptyStateBlock text={error} />
          ) : casesData.closed.length ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: 12,
              }}
            >
              {casesData.closed.map((item) => (
                <CaseCard key={item.id} item={item} onOpenCase={onOpenCase} />
              ))}
            </div>
          ) : (
            <EmptyStateBlock text="No hay casos cerrados de esta especialidad para este paciente." />
          )}
        </SectionCard>
      </div>
    </div>
  )
}
